import * as React from "react";

export type EntitlementEventSource = "apphud" | "manual" | "webhook";

export interface EntitlementHistoryItem {
  type: "subscription_started" | "subscription_expired" | "grant" | "revoke" | "webhook";
  date: string;
  source: EntitlementEventSource;
  operator: string | null;
}

const EVENT_TYPE_LABELS: Record<EntitlementHistoryItem["type"], string> = {
  subscription_started: "Подписка активирована",
  subscription_expired: "Подписка истекла",
  grant: "Компенсация",
  revoke: "Отмена подписки",
  webhook: "Webhook-событие",
};

const DOT_CLASSES: Record<EntitlementHistoryItem["type"], string> = {
  subscription_started: "border-[var(--accent)] bg-[var(--accent-weak)]",
  subscription_expired: "border-[var(--danger)] bg-[rgba(196,58,42,0.10)]",
  grant: "border-[var(--warn)] bg-[rgba(196,138,26,0.10)]",
  revoke: "border-[var(--danger)] bg-[rgba(196,58,42,0.10)]",
  webhook: "border-[var(--border)] bg-[var(--surface)]",
};

function SourceBadge({ source }: { source: EntitlementEventSource }): React.JSX.Element {
  // Darker text on tinted backgrounds for WCAG 2.2 AA 4.5:1 contrast on small text.
  const variants: Record<EntitlementEventSource, string> = {
    apphud:
      "text-[#0055aa] bg-[rgba(0,113,227,0.06)] border-[rgba(0,113,227,0.15)]",
    manual:
      "text-[#7a5200] bg-[rgba(196,138,26,0.06)] border-[rgba(196,138,26,0.15)]",
    webhook:
      "text-[var(--muted)] bg-[var(--surface-2)] border-[var(--border)]",
  };

  const labels: Record<EntitlementEventSource, string> = {
    apphud: "App Store",
    manual: "manual",
    webhook: "webhook",
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-[4px] text-[11px] font-[600] border ${variants[source]}`}
    >
      {labels[source]}
    </span>
  );
}

interface EntitlementEventItemProps {
  event: EntitlementHistoryItem;
}

export function EntitlementEventItem({
  event,
}: EntitlementEventItemProps): React.JSX.Element {
  const operator = event.operator;
  const formattedDate = new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(new Date(event.date));

  return (
    <div className="relative pl-7 pb-7 last:pb-0" role="listitem">
      <div
        className={`absolute left-0 top-1 w-4 h-4 rounded-full border-2 z-[1] ${DOT_CLASSES[event.type]}`}
        aria-hidden="true"
      />
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] px-4 py-3.5 hover:shadow-sm transition-shadow">
        <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
          <span className="text-[14px] font-[600] text-[var(--fg)]">
            {EVENT_TYPE_LABELS[event.type]}
          </span>
          <SourceBadge source={event.source} />
          <span className="ml-auto text-[12px] text-[var(--muted)] font-mono">
            {formattedDate}
          </span>
        </div>
        {operator && (
          <div className="text-[12px] text-[var(--muted)]">
            Оператор: <strong className="text-[var(--fg)] font-[500]">{operator}</strong>
          </div>
        )}
      </div>
    </div>
  );
}
